<script>
import { mapGetters } from 'vuex';

export default {
  name: 'CoveragePricing',
  props: {
    quoteState: {
      type: String,
      required: true
    },
    vehicleIndex: {
      type: Number,
      required: true
    },
    isUnlimited: {
      type: Boolean
    },
    singlePaymentOption: {
      type: String
    },
    // Fee-inclusive monthly figure; the caller adds the state's monthly fees so
    // the total tracks the Pay-Per-Mile / Unlimited selection.
    estimatedMonthlyPayment: {
      type: String
    },
    monthlyPayDownPayment: {
      type: String
    },
    feeTooltip: {
      type: String,
      default: ''
    }
  },
  data: () => ({}),
  computed: {
    ...mapGetters(['quoteForm', 'quoteResponse']),
    currentQuoteVehicle() {
      return this.quoteForm.vehicles[this.vehicleIndex];
    },
    currentResponseVehicle() {
      let currentVehicle = this.quoteResponse &&
        this.quoteResponse.policies[0] &&
        this.quoteResponse.policies[0].vehicleInfo[this.vehicleIndex];

      return currentVehicle ? currentVehicle : null;
    }
  }
};
</script>

<template>
  <div style="margin-top:-2rem">
    <div class="box -spacing-static-e cost-container__vehicle">
      <div class="quote-type-header">
        <h3 class="heading right-section-first-container">
          Select Payment Plan for this vehicle:
          <i class="icon icon--info tool-tip">
            <div class="content">
              Select the Pay-Per-Mile or Unlimited option individually for each
              vehicle. You may change your selections at each 6-month renewal.
              <i></i>
            </div>
          </i>
        </h3>
        <div
          :class="{
            'policy-type-select': true
          }"
        >
          <div
            :class="{
              'policy-type-select-tab': true,
              'policy-type-select__ppm': true,
              active: !currentQuoteVehicle.unlimitedVehicle && !isUnlimited
            }"
            @click="$emit('toggleUnlimited', vehicleIndex)"
          >
            <h3>Pay-Per-Mile</h3>
          </div>
          <div
            :class="{
              'policy-type-select-tab': true,
              'policy-type-select__unlimited': true,
              active: currentQuoteVehicle.unlimitedVehicle && isUnlimited
            }"
            @click="$emit('toggleUnlimited', vehicleIndex)"
          >
            <h3>Unlimited</h3>
          </div>
        </div>
        <div class="policy-type-text">
          <p v-show="!isUnlimited">
            With Pay-Per-Mile, your premium will vary based on the number of
            miles you actually drive. When it’s time to report your mileage, we
            will contact you by text and email with an automated web link that
            makes it easy for you to snap and send a photo of your odometer.
          </p>
          <p v-show="!isUnlimited">
            If you drive this vehicle less than 14,000 miles per year, we
            recommend the Pay-Per-Mile option.
          </p>
          <p v-show="isUnlimited">
            With Unlimited, your premium is fixed for the 6-month policy term
            and you are not required to report your mileage. If you drive this
            vehicle more than 14,000 miles per year or prefer not to send
            odometer readings, we recommend the Unlimited option.
          </p>
          <p v-show="isUnlimited">
            To provide you with the best service possible, we will ask for your
            vehicle’s current odometer reading at the start of each policy term
            via an automated web link.
          </p>
        </div>
      </div>
      <div
        v-show="currentQuoteVehicle && !currentQuoteVehicle.unlimitedVehicle"
        class="quote-premium quote-premium__ppm"
      >
        <h3 class="heading" style="padding-top:2rem;">Vehicle Premium</h3>

        <div class="columns is-mobile">
          <div class="column is-7">Base Charge:</div>
          <div class="column is-5">
            <span v-if="quoteResponse">
              ${{ currentResponseVehicle.premiumInfo.baseRate }}
            </span>
            <span v-else>-</span>&nbsp;per month
          </div>
        </div>

        <div class="columns is-mobile">
          <div class="column is-7">Per Mile Charge:</div>
          <div class="column is-5">
            <span v-if="quoteResponse">
              ${{
                currentResponseVehicle &&
                  currentResponseVehicle.premiumInfo.perMileRate.toFixed(3)
              }}
            </span>
            <span v-else>-</span>&nbsp;per mile
          </div>
        </div>

        <!--      <div class="columns is-mobile">-->
        <!--        <div class="column is-7">Estimated monthly premium:</div>-->
        <!--        <div class="column is-5">-->
        <!--          <span v-if="quoteResponse">-->
        <!--            ${{ currentResponseVehicle && currentResponseVehicle.premiumInfo.estMonthlyNoFee }}-->
        <!--          </span>-->
        <!--          <span v-else>-</span>-->
        <!--        </div>-->
        <!--      </div>-->

        <div v-if="quoteResponse && currentResponseVehicle && currentResponseVehicle.premiumInfo" class="columns is-mobile">
          <div class="column is-7">Estimated monthly miles:</div>
          <div class="column is-5">
            <span v-if="quoteResponse && currentResponseVehicle && currentResponseVehicle.premiumInfo && currentResponseVehicle.premiumInfo.estMonthlyMiles">
              {{
                  currentResponseVehicle.premiumInfo.estMonthlyMiles
              }}
            </span>
            <span v-else>-</span>
          </div>
        </div>
      </div>
      <div
        v-show="currentQuoteVehicle && currentQuoteVehicle.unlimitedVehicle"
        class="quote-premium quote-premium__unlimited"
      >
        <h3 class="heading">Vehicle Premium</h3>

        <div class="columns is-mobile">
          <div class="column is-7">Base Charge:</div>
          <div class="column is-5">
            <span v-if="quoteResponse">
              ${{
                (
                  (currentResponseVehicle &&
                    currentResponseVehicle.unlimitedRate) ||
                  0
                ).toFixed(2)
              }}
            </span>
            <span v-else>-</span>&nbsp;per month
          </div>
        </div>
      </div>

      <!-- Policy-level totals: shown for both Pay-Per-Mile and Unlimited -->
      <div v-if="!isUnlimited" class="quote-premium quote-premium__totals">
        <h3 class="heading">Total Policy Premium</h3>
        <div class="columns is-mobile">
          <div class="column is-7">
            Estimated Monthly Payment (includes fees):
            <i class="icon icon--info tool-tip">
              <div class="content">
                <p>{{ feeTooltip }}</p>
                <i></i>
              </div>
            </i>
          </div>
          <div
            v-if="quoteResponse"
            class="column is-5"
            style="position:relative;"
          >
            <span style="position:absolute; bottom:0;">
              ${{ estimatedMonthlyPayment }}
            </span>
          </div>
          <div v-else class="column is-4">-</div>
        </div>

        <h3 class="heading">Down Payment</h3>
        <div class="columns is-mobile">
          <div class="column is-7">Due Today:</div>
          <div v-if="quoteResponse" class="column is-5">
            ${{ monthlyPayDownPayment }}
            <i class="icon icon--info tool-tip">
              <div class="content">
                <p>{{ feeTooltip }}</p>
                <i></i>
              </div>
            </i>
          </div>
          <div v-else class="column is-4">-</div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.quote-premium {
    margin: 1rem auto 2rem;
}
.heading {
  font-family: $family-sans-serif-bold;
  text-transform: none;
  letter-spacing: 0;
  font-size: inherit;
  margin-bottom: 1rem;

  &:last-of-type {
    border-top: 1px solid $mischka;
    margin-top: 2rem;
  }
}

.policy-type-select {
  color: $bombay;
  display: flex;
  align-items: center;
  background-color: $white;
  &__unlimited {
    border-left: solid 1px $mischka;
  }

  .policy-type-select-tab {
    text-align: center;
    width: 50%;

    h3 {
      font-family: PorscheNextBold, Arial, sans-serif;
      letter-spacing: 0;
      font-weight: 400;
    }
    &.active {
      border-top: 1px solid $mischka;
      border-bottom: 1px solid white;
    }
    &:not(.active) {
      border-top: 1px solid white;
      border-bottom: 1px solid $mischka;
    }
  }
  .active {
    color: $primary;
  }
  div {
    white-space: nowrap;
    .content {
      border: none;
    }
  }
}
.cost-container {
  @media (min-width: 1000px) {
    margin-top: 1.5rem;
  }
}
.estimated-cost {
  font-family: $family-sans-serif-bold;
  text-transform: none;
  letter-spacing: 0;
  font-size: inherit;
}

.right-section-first-container {
  border-top: none !important;
  margin-top: 0 !important;
}
.tool-tip {
  color: #737278;
  width: 10px;
  top: 0;
  left: 0;
  .content {
    @media (min-width: 992px) {
      width: 420px;
    }
    @media (max-width: 991px) {
      width: 415px;
      left: 8px;
      i {
        left: 50%;
      }
    }
    @media (min-width: 992px) {
      left: 50%;
    }
    @media (max-width: 760px) {
      width: 300px;
      left: -15px;
      i {
        left: 57%;
      }
    }
  }
}
</style>
